import type { ZodIssueMetadata } from '@safc/configuration';
import {
  BaselineCarbonIntensity,
  ConversionProcess,
  conversionProcessToLabel,
  type Country,
  getFeedstockFieldsToSet,
  JetFuelBaselineCarbonIntensityToLabel,
  NeatFuelVolumeUnit,
} from '@safc/configuration';
import {
  feedstockDatabase,
  feedstockDatabaseSelectList,
  feedstockOther,
} from '@safc/configuration';
import { FormCountry } from 'components/ui/controlled-form/FormCountry/FormCountry.js';
import { FormSelect } from 'components/ui/controlled-form/FormSelect/FormSelect.js';
import { RadioGroupForm } from 'components/ui/controlled-form/RadioGroupForm/RadioGroupForm.js';
import { FormInput } from 'components/ui/form/FormInput/FormInput.js';
import { InputAdornmentText } from 'components/ui/form/Input/Input.styled.js';
import { useEffect } from 'react';
import type {
  Control,
  UseFormSetValue,
} from 'react-hook-form';

import { IssueFormRow } from '../components/IssueFormRow.js';
import { IssueFormYesNo } from '../components/IssueFormYesNo.js';

type Props = {
  control: Control<any>;
  volumeOfNeatFuel: number | undefined;
  feedstockName: string | undefined;
  isSolidWaste: boolean | undefined;
  conversionProcess: ConversionProcess | undefined;
  feedstockCountries: Country[] | undefined;
  setValue: UseFormSetValue<ZodIssueMetadata>;
};

export const IssueFormProduct = (props: Props) => {
  const { control, volumeOfNeatFuel, feedstockName, isSolidWaste, setValue, conversionProcess, feedstockCountries } = props;

  const onFeedstockNameChange = (feedstockName: string) => {
    // feedstock (data/settings) needs to be synchronized upon selecting feedstock by name
    const fieldsToSet = getFeedstockFieldsToSet(feedstockName);
    for (const [path, value] of fieldsToSet) {
      setValue(path, value);
    }
  };

  useEffect(() => {
    if (feedstockName === undefined || conversionProcess === undefined || feedstockCountries === undefined || feedstockCountries.length === 0) {
      setValue('product.feedStock.isCorsiaDefaultIlucLcaLteZero', undefined as unknown as boolean);
      return;
    }

    const feedstock = feedstockDatabase.find(entry => entry.name === feedstockName);

    if (!feedstock) {
      throw new Error(`Cannot find feedstock: ${feedstockName} in feedstock database`);
    }

    const isCorsiaDefaultIlucLcaLteZero = feedstock.corsiaDefaultIlucLcaLteZeroCb({ conversionProcess, feedstockCountries });
    setValue('product.feedStock.isCorsiaDefaultIlucLcaLteZero', isCorsiaDefaultIlucLcaLteZero);
  }, [feedstockName, conversionProcess, feedstockCountries]);

  return (
    <>
      <IssueFormRow control={control} field="product.quantityMT">
        {props => (
          <FormInput
            control={props.control}
            field={{
              ...props.field,
              type: 'number',
              componentProps: {
                decimalPoints: 3,
              },
              endAdornment: <InputAdornmentText>MT</InputAdornmentText>,
            }}
          />
        )}
      </IssueFormRow>
      <IssueFormRow control={control} field="product.volumeOfNeatFuel">
        {props => (
          <FormInput
            control={props.control}
            field={{
              ...props.field,
              type: 'number',
              componentProps: {
                decimalPoints: 3,
              },
            }}
          />
        )}
      </IssueFormRow>
      {volumeOfNeatFuel !== undefined && (
        <IssueFormRow control={control} field="product.volumeOfNeatFuelUnit">
          {props => (
            <RadioGroupForm
              control={props.control}
              field={{
                ...props.field,
                options: [
                  { label: 'gallons', value: NeatFuelVolumeUnit.Gallons },
                  { label: 'liters', value: NeatFuelVolumeUnit.Liters },
                ],
              }}
            />
          )}
        </IssueFormRow>
      )}
      <IssueFormRow control={control} field="product.feedStock.name">
        {props => (
          <FormSelect
            control={props.control}
            field={{
              ...props.field,
              onAfterChangeHook: (e: any) => onFeedstockNameChange(e.target.value),
              options: feedstockDatabaseSelectList,
            }}
          />
        )}
      </IssueFormRow>
      {feedstockName === feedstockOther.name && (
        <IssueFormRow control={control} field="product.feedStock.otherFeedStock">
          {props => (<FormInput {...props} />)}
        </IssueFormRow>
      )}
      <IssueFormRow control={control} field="product.countries">
        {props => (<FormCountry control={props.control} field={{ ...props.field, componentProps: { multiple: true } }} />)}
      </IssueFormRow>
      <IssueFormRow control={control} field="product.conversionProcess">
        {props => (
          <FormSelect
            control={props.control}
            field={{
              ...props.field,
              options: Object.values(ConversionProcess).map(value => ({
                value,
                label: conversionProcessToLabel[value],
              })),
            }}
          />
        )}
      </IssueFormRow>
      {feedstockName !== undefined && (
        <>
          <IssueFormRow control={control} field="product.feedStock.isSolidWaste">
            {props => (
              <IssueFormYesNo
                control={props.control}
                field={{
                  ...props.field,
                  disabled: feedstockName !== feedstockOther.name,
                }}
              />
            )}
          </IssueFormRow>
          <IssueFormRow control={control} field="product.feedStock.wasCertifiedAsProductionResidue">
            {props => (
              <IssueFormYesNo
                control={props.control}
                field={{
                  ...props.field,
                  disabled: feedstockName !== feedstockOther.name,
                }}
              />
            )}
          </IssueFormRow>
          <IssueFormRow control={control} field="product.feedStock.isEnergyCrop">
            {props => (
              <IssueFormYesNo
                control={props.control}
                field={{
                  ...props.field,
                  disabled: feedstockName !== feedstockOther.name,
                }}
              />
            )}
          </IssueFormRow>
          <IssueFormRow control={control} field="product.feedStock.isLanduseBasedCropBased">
            {props => (
              <IssueFormYesNo
                control={props.control}
                field={{
                  ...props.field,
                  disabled: feedstockName !== feedstockOther.name,
                }}
              />
            )}
          </IssueFormRow>
          <IssueFormRow control={control} field="product.feedStock.isCorsiaDefaultIlucLcaLteZero">
            {props => (
              <IssueFormYesNo
                control={props.control}
                field={{
                  ...props.field,
                  disabled: feedstockName !== feedstockOther.name,
                }}
              />
            )}
          </IssueFormRow>
          <IssueFormRow control={control} field="product.feedStock.isInEuRed2Annex9PartA">
            {props => (
              <IssueFormYesNo
                control={props.control}
                field={{
                  ...props.field,
                  disabled: feedstockName !== feedstockOther.name,
                }}
              />
            )}
          </IssueFormRow>
        </>
      )}
      {isSolidWaste === true && (
        <IssueFormRow control={control} field="product.isFeedStockMsw">
          {props => (
            <FormInput
              control={props.control}
              field={{
                ...props.field,
                type: 'number',
                endAdornment: <InputAdornmentText>%</InputAdornmentText>,
              }}
            />
          )}
        </IssueFormRow>
      )}
      <IssueFormRow control={control} field="product.lowerHeatingValue" isNumber>
        {props => (
          <FormInput
            control={props.control}
            field={{
              ...props.field,
              type: 'number',
              endAdornment: <InputAdornmentText>MJ/kg</InputAdornmentText>,
            }}
          />
        )}
      </IssueFormRow>
      <IssueFormRow control={control} field="product.jetFuelBaselineCarbonIntensity" isNumber>
        {props => (
          <FormSelect
            control={props.control}
            field={{
              ...props.field,
              endAdornment: <InputAdornmentText>gCO2e/MJ</InputAdornmentText>,
              options: Object.values(BaselineCarbonIntensity).map(value => ({
                value,
                label: JetFuelBaselineCarbonIntensityToLabel[value],
              })),
            }}
          />
        )}
      </IssueFormRow>
    </>
  );
};
